/**
 * Workspace search for the command palette.
 *
 * Mirrors `securities-search.ts`: backed by `GET /api/v1/search/workspaces`
 * (org-scoped server-side), fires only once the caller has typed at least two
 * characters, and never retries on auth failures or an unready index.
 */

import { useQuery } from "@tanstack/react-query";
import { devToken } from "@itoresearch/api-client";

import { api, ApiError } from "../../lib/api-client";

export function useWorkspacesSearch(raw: string) {
  const q = raw.trim();
  return useQuery({
    queryKey: ["search", "workspaces", q],
    enabled: q.length >= 2,
    staleTime: 30_000,
    // Auth failures (401) and an unready index (503) won't resolve within
    // seconds — don't burn retries on them.
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 500 && failureCount < 2,
    queryFn: () =>
      api.search.workspaces(
        { q, limit: 8 },
        // Local dev / e2e: the API accepts a `Bearer dev.<user>.<org>` token
        // instead of a session cookie, which the web app doesn't have in dev.
        import.meta.env.DEV ? devToken() : undefined,
      ),
  });
}
